/**
 * Step 3: the runner — who answers each item. Either an agent, run now (the
 * connection, then the platform's own screens, looked up by the connection's
 * connector type: what to test, then the agent's inputs, led by where each
 * comes from), or the outputs already in the dataset (an upload's output
 * column, or a live dataset's traces), scored without calling anything.
 */

import type { ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/states";
import { useRequestPreview } from "@/features/connectors/hooks";
import { PRODUCTION } from "@/features/connectors/types";
import { credentialRefsPayload, runSettingsPayload } from "../run-package";
import { inputMappingPayload } from "./input-mapping";
import { InputMappingStep } from "./input-mapping-step";
import { RunnerChoice } from "./runner-choice";
import { StepConnection } from "./step-connection";
import type { NewExperiment } from "./use-new-experiment";

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title} className="flex flex-col gap-3">
      <h2 className="text-[13px] font-semibold">{title}</h2>
      {children}
    </section>
  );
}

export function StepRunner({ flow, locked }: { flow: NewExperiment; locked: boolean }) {
  return (
    <>
      <RunnerChoice flow={flow} locked={locked} />
      {flow.runner === "agent" && <AgentRunner flow={flow} locked={locked} />}
    </>
  );
}

/** Connection, then the platform's picker, side by side as one column browser. */
function AgentRunner({ flow, locked }: { flow: NewExperiment; locked: boolean }) {
  const discovers = !!flow.target.screens?.discovers;
  return (
    <>
      <Section title="What to test">
        <div className="grid overflow-hidden rounded-[9px] border border-line bg-surface md:grid-cols-[minmax(0,1fr)_minmax(0,3fr)]">
          <div className="min-w-0 max-md:border-b md:border-r border-line-soft">
            <StepConnection flow={flow} />
          </div>
          <div className={`min-w-0 ${discovers ? "" : "p-4"}`}>
            {flow.connection ? (
              <StepTarget flow={flow} locked={locked} />
            ) : (
              <p className="flex h-full min-h-[132px] items-center justify-center text-xs text-faint">
                Pick a connection first
              </p>
            )}
          </div>
        </div>
        <AgentNote flow={flow} />
      </Section>
      {/* A region without a heading: the inputs step shows its own "Agent inputs". */}
      {flow.target.ready && (
        <section aria-label="Agent inputs" className="flex flex-col gap-3">
          <StepRunSettings flow={flow} />
        </section>
      )}
    </>
  );
}

/** The picked agent, as a path, under the browser: where each item will be sent. */
function AgentNote({ flow }: { flow: NewExperiment }) {
  const { connection, target } = flow;
  if (!connection || !target.ready) return null;
  const parts = [
    connection.name,
    target.resolved.target,
    target.resolved.version === PRODUCTION ? "production" : `v${target.resolved.version}`,
  ];
  return (
    <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
      <span className="text-faint">Each item goes to</span>
      {parts.filter(Boolean).map((p, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {i > 0 && <ChevronRight aria-hidden className="size-3 text-dim" />}
          <span className="font-medium text-body">{p}</span>
        </span>
      ))}
    </p>
  );
}

function StepTarget({ flow, locked }: { flow: NewExperiment; locked: boolean }) {
  const { connection, target } = flow;
  if (!connection || !target.screens) return <Skeleton label="Loading the platform" />;
  const { TargetPicker } = target.screens;
  return (
    <TargetPicker
      key={connection.id}
      connection={connection}
      value={target.resolved}
      onChange={flow.changeTarget}
      locked={locked}
    />
  );
}

function StepRunSettings({ flow }: { flow: NewExperiment }) {
  const { connection, target, form, mapping } = flow;
  const spec = target.spec.data;
  const canPreview = !!flow.connector?.capabilities.includes("preview");
  const preview = useRequestPreview(
    canPreview ? (flow.connector?.type ?? null) : null,
    mapping.first && {
      item_input: mapping.first.input,
      input_mapping: inputMappingPayload(mapping.mapping) ?? {},
      run_settings: runSettingsPayload(spec, form.runSettings),
      secret_keys: Object.keys(credentialRefsPayload(spec, form.credentialRefs)),
    },
  );
  if (!connection || !target.screens || target.spec.isPending)
    return <Skeleton label="Loading the agent's inputs" />;
  if (target.spec.isError)
    return (
      <p role="alert" className="rounded-lg bg-err-bg px-3.5 py-2.5 text-[13px] text-err">
        Could not read this version's run settings from the platform. You can still save a draft.
      </p>
    );
  const { RunSettingsForm } = target.screens;
  return (
    <>
      <InputMappingStep
        inputs={mapping.inputs}
        fields={mapping.fields}
        mapping={mapping.mapping}
        onChange={form.setInputMapping}
        credentials={{
          refs: form.credentialRefs,
          set: form.setCredentialRef,
          clear: form.clearCredentialRef,
        }}
        preview={canPreview ? preview : undefined}
      />
      <RunSettingsForm
        connectorType={connection.connector_type}
        spec={target.spec.data}
        values={form.runSettings}
        refs={form.credentialRefs}
        onSetting={form.setRunSetting}
        onCredential={form.setCredentialRef}
      />
    </>
  );
}
